"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HiPlus, HiMinus, HiArrowRight } from "react-icons/hi";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { getFeaturedFaq } from "@/data/faq";
import { useLanguage, useTranslations } from "@/lib/i18n";

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { language } = useLanguage();
  const t = useTranslations();
  const homepageFaqs = getFeaturedFaq(language).slice(0, 5);

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-24 md:py-32 bg-white border-t border-gray-100">
      <div className="container mx-auto px-6 md:px-12">
        <div className="flex flex-col lg:flex-row gap-16 md:gap-24">
          {/* HEADER SECTION */}
          <div className="w-full lg:w-1/3">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-brand-gold text-xs font-bold tracking-[0.25em] uppercase block mb-4 font-sans">
                {t.faq.tagline}
              </span>
              <h2 className="font-serif text-4xl md:text-5xl text-brand-charcoal mb-6 whitespace-pre-line">
                {t.faq.title}
              </h2>
              <p className="text-gray-500 font-light text-lg leading-relaxed mb-8 font-sans">
                {t.faq.subtitle}
              </p>

              {/* Premium Badge */}
              <div className="flex items-center gap-3 mt-6">
                <div className="w-8 h-8 rounded-full bg-brand-gold/10 flex items-center justify-center">
                  <span className="text-brand-gold text-sm font-bold font-sans">
                    ✓
                  </span>
                </div>
                <span className="text-sm text-gray-600 font-sans">
                  {t.faq.badge}
                </span>
              </div>
            </motion.div>
          </div>

          {/* ACCORDION LIST */}
          <div className="w-full lg:w-2/3">
            <div className="space-y-4 mb-10">
              {homepageFaqs.map((faq, index) => {
                const isOpen = openIndex === index;

                return (
                  <motion.div
                    key={faq.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    className={`border-b border-gray-100 pb-4 ${
                      isOpen
                        ? "bg-gray-50/50 rounded-lg p-6 border-none"
                        : "px-2"
                    } transition-all duration-300`}
                  >
                    <button
                      onClick={() => toggleAccordion(index)}
                      className="w-full flex justify-between items-center text-left py-2 group"
                    >
                      <h3
                        className={`font-medium text-lg md:text-xl transition-colors font-sans ${
                          isOpen
                            ? "text-brand-gold"
                            : "text-brand-charcoal group-hover:text-brand-gold"
                        }`}
                      >
                        {faq.question}
                      </h3>
                      <span
                        className={`ml-4 flex-shrink-0 transition-transform duration-300 ${
                          isOpen ? "rotate-180 text-brand-gold" : "text-gray-300"
                        }`}
                      >
                        {isOpen ? <HiMinus size={20} /> : <HiPlus size={20} />}
                      </span>
                    </button>

                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          className="overflow-hidden"
                        >
                          <p className="text-gray-500 font-light leading-relaxed text-base pr-8 pb-2 pt-2 font-sans">
                            {faq.answer}
                          </p>

                          {/* Category Tag */}
                          <div className="flex items-center gap-2 mt-4">
                            <span className="text-xs px-3 py-1 bg-brand-gold/10 text-brand-gold rounded-full font-sans font-medium">
                              {faq.category}
                            </span>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>

            {/* TOMBOL LIHAT SEMUA */}
            <div className="flex justify-start px-2">
              <Link href="/faq">
                <Button
                  variant="link"
                  className="text-brand-charcoal text-xs font-bold tracking-[0.2em] uppercase hover:text-brand-gold transition-colors p-0 flex items-center gap-2 group font-sans"
                >
                  {t.faq.exploreAll}
                  <HiArrowRight
                    size={16}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
